import { Col, Empty, Input, Row, Select, Spin } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GENERAL_TIMEOUT } from '../../constants/systemConstants';
import { GoodData } from '../../types';
import { alertMessage } from '../../utils/alertMessage';
import { getDataWithAuthToken } from '../../utils/axiosRequest';
import getDashboardStyle from '../../utils/getDashboardStyle';
import BrandDropdown from '../goods/common/BrandDropdown';
import CategoryDropdown from '../goods/common/CategoryDropdown';
import GoodCard from '../goods/common/GoodCard';
import SelectGoodAttrModal from '../goods/selectGoods/SelectGoodAttrModal';

const PAGE_SIZE = 24;

type PosProductGridProps = {
  sellerId: number;
  userId: number;
  formData: { [key: string]: any };
  setFormData: React.Dispatch<React.SetStateAction<{ [key: string]: any }>>;
};

/**
 * Quick product selection for the POS terminal.
 *
 * Reuses goods/list (same params as SelectGoodsModal). The grid is responsive
 * (more columns on bigger screens) and uses infinite scroll: the next page is
 * loaded when the user scrolls near the bottom. Tapping a card opens
 * SelectGoodAttrModal which handles SKU/price/qty/weight and writes the
 * recalculated orderGoodsList + total back into formData via order/preview_goods.
 */
const PosProductGrid = ({
  sellerId,
  userId,
  formData,
  setFormData,
}: PosProductGridProps) => {
  const { t } = useTranslation();
  const [goodData, setGoodData] = useState<GoodData[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [catId, setCatId] = useState<number>();
  const [brandId, setBrandId] = useState<number>();
  const [isOnSale, setIsOnSale] = useState<boolean | undefined>(
    !getDashboardStyle().isWholeSaleAppType ? true : undefined
  );
  const [currentGood, setCurrentGood] = useState<GoodData>();
  const [attrModalVisible, setAttrModalVisible] = useState(false);
  const typingTimeout = useRef<NodeJS.Timeout>();
  const isSubscribed = useRef(true);
  const fetching = useRef(false);

  useEffect(() => {
    return () => {
      isSubscribed.current = false;
    };
  }, []);

  const getGoods = useCallback(
    (targetPage: number, replace: boolean) => {
      if (fetching.current) return;
      fetching.current = true;
      if (replace) setLoading(true);
      else setLoadingMore(true);
      getDataWithAuthToken('goods/list', {
        params: {
          page: targetPage,
          size: PAGE_SIZE,
          sellerId,
          catId: catId || undefined,
          brandId: brandId || undefined,
          keyword: keyword || undefined,
          isOnSale: typeof isOnSale === 'boolean' ? isOnSale : undefined,
        },
      })
        .then((res) => {
          if (!isSubscribed.current) return;
          if (res && res.goodStatus) {
            const list: GoodData[] = res.data.list;
            setTotal(res.data.totalItem);
            setPage(targetPage);
            setGoodData((prev) => (replace ? list : [...prev, ...list]));
          } else {
            alertMessage(
              'error',
              res?.msg || t('general.noResponse'),
              res?.data || undefined
            );
          }
        })
        .catch((err) => console.log(err))
        .finally(() => {
          fetching.current = false;
          if (isSubscribed.current) {
            setLoading(false);
            setLoadingMore(false);
          }
        });
    },
    [sellerId, catId, brandId, keyword, isOnSale, t]
  );

  // Re-fetch from page 1 whenever the store or any filter changes
  useEffect(() => {
    getGoods(1, true);
  }, [getGoods]);

  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (
      el.scrollTop + el.clientHeight >= el.scrollHeight - 120 &&
      !fetching.current &&
      goodData.length < total
    ) {
      getGoods(page + 1, false);
    }
  };

  const onSearch = (value: string) => {
    if (typingTimeout.current) clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => setKeyword(value), GENERAL_TIMEOUT);
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Same order as SelectGoodsModal: category, brand, keyword, on sale */}
      <Row gutter={[8, 8]} style={{ padding: 12 }}>
        <Col xs={12} sm={6}>
          <CategoryDropdown
            onFocusFetch
            onChange={(value: any) =>
              setCatId(
                value && value.length ? value[value.length - 1] : undefined
              )
            }
          />
        </Col>
        <Col xs={12} sm={6}>
          <BrandDropdown onChange={(value) => setBrandId(value || undefined)} />
        </Col>
        <Col xs={24} sm={8}>
          <Input.Search
            allowClear
            placeholder={t('searchPlaceholders.searchNameSnBarcodeKeywords')}
            onChange={(e) => onSearch(e.target.value)}
            onSearch={(value) => setKeyword(value)}
          />
        </Col>
        <Col xs={24} sm={4}>
          <Select
            style={{ width: '100%' }}
            value={isOnSale}
            allowClear
            placeholder={t('goods.goodsListColumns.isOnSale')}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
            onChange={(value) => setIsOnSale(value)}
          >
            <Select.Option value={true}>
              {t('general.booleanToStatus.true')}
            </Select.Option>
            <Select.Option value={false}>
              {t('general.booleanToStatus.false')}
            </Select.Option>
          </Select>
        </Col>
      </Row>

      <div
        onScroll={onScroll}
        style={{ flex: 1, overflow: 'auto', padding: '0 12px 12px' }}
      >
        {loading && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        )}
        {!loading && !goodData.length && <Empty style={{ marginTop: 40 }} />}
        {!loading && (
          <Row gutter={[12, 12]}>
            {goodData.map((good, index) => {
              const disabled = good.isDelete;
              return (
                <Col
                  key={`${good.goodsId}_${index}`}
                  xs={12}
                  sm={8}
                  md={8}
                  lg={6}
                  xl={4}
                  xxl={4}
                >
                  <GoodCard
                    good={good}
                    disabled={disabled}
                    onClick={() => {
                      setCurrentGood(good);
                      setAttrModalVisible(true);
                    }}
                  />
                </Col>
              );
            })}
          </Row>
        )}
        {loadingMore && (
          <div style={{ textAlign: 'center', padding: 12 }}>
            <Spin />
          </div>
        )}
      </div>

      {attrModalVisible && (
        <SelectGoodAttrModal
          visible={attrModalVisible}
          setVisible={setAttrModalVisible}
          selectedGoods={formData.orderGoodsList}
          setSelectedGoods={setFormData}
          callBack={() => setCurrentGood(undefined)}
          userId={userId}
          currentGood={currentGood}
        />
      )}
    </div>
  );
};

export default PosProductGrid;
